import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import apiClient from '../services/apiClient';

interface HeroData {
  title: string;
  subtitle: string;
  badgeText: string;
  badgeLabel: string;
  userCount: number;
}

const Hero = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [heroData, setHeroData] = useState<HeroData>({
    title: 'Developer Marketplace',
    subtitle: 'Find the perfect assets for your next project. Created by developers, for developers.',
    badgeText: 'Check out the latest marketplace items',
    badgeLabel: '✨ New',
    userCount: 2500
  });
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Fetch hero data
  useEffect(() => {
    const fetchHeroData = async () => {
      try {
        const response = await apiClient.get('/site/hero');
        if (response.data) {
          setHeroData(response.data);
        }
      } catch (error) {
        console.error('Error fetching hero data:', error);
        // Keep default values on error
      }
    };

    fetchHeroData();
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Redirect to search results page
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
  };

  const handleExploreClick = () => {
    navigate('/search');
  };

  const handleLearnMoreClick = () => {
    navigate('/about');
  };

  return (
    <section className="relative h-[650px] overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1B1821] via-[#2A1E3F] to-[#1B1821] bg-size-200 animate-gradient-x"></div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('https://cdn.jsdelivr.net/npm/pattern.css@1.0.0/img/patterns/grid.svg')] opacity-5"></div>

      {/* Radial glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#7F56D9]/20 blur-[100px]"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#F670C7]/20 blur-[100px]"></div>

      {/* Animated particle effects */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white opacity-20"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              animation: `floatParticle ${Math.random() * 8 + 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Content container */}
      <div className="relative z-10 container mx-auto px-4 h-full flex flex-col md:flex-row items-center justify-between">
        {/* Text content */}
        <div className={`max-w-2xl pt-16 md:pt-0 transition-all duration-1000 ease-out transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-[#2A2633] border border-[#3A3643] hover:bg-[#342D3F] transition-colors">
            <span className="text-[#F670C7] font-medium mr-2">{heroData.badgeLabel}</span>
            <span className="text-[#A3A3A3]">{heroData.badgeText}</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7F56D9] via-[#EA4CFF] to-[#F670C7] animate-gradient-x bg-size-200">
              {heroData.title}
            </span>
          </h1>

          <p className="text-xl mb-8 text-[#A3A3A3] max-w-xl">
            {heroData.subtitle}
          </p>

          {/* Search bar */}
          <form onSubmit={handleSearchSubmit} className="relative mb-8 max-w-xl">
            <div className="flex overflow-hidden rounded-lg border border-[#3A3643] focus-within:border-[#7F56D9] transition-colors shadow-lg">
              <input
                type="text"
                placeholder="Search for assets, plugins, or themes..."
                className="flex-1 py-3 px-4 bg-[#2A2633] text-white outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                type="submit"
                className="bg-[#7F56D9] text-white px-5 flex items-center justify-center hover:bg-[#6941C6] transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </form>

          <div className="flex flex-wrap gap-4 mb-6">
            <Button
              variant="primary"
              size="lg"
              className="px-8"
              onClick={handleExploreClick}
            >
              Explore Marketplace
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="px-8"
              onClick={handleLearnMoreClick}
            >
              Learn More
            </Button>
          </div>

          <div className="flex items-center gap-4 mt-8">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-[#1B1821] bg-gradient-to-br from-purple-500 to-pink-500"
                  style={{
                    background: `linear-gradient(135deg, ${i === 1 ? '#7F56D9' : i === 2 ? '#9E77ED' : i === 3 ? '#D6BBFB' : '#F670C7'}, ${i === 1 ? '#954ADF' : i === 2 ? '#C288FD' : i === 3 ? '#EFD7FF' : '#FF9AD5'})`,
                    zIndex: 5 - i
                  }}
                />
              ))}
            </div>
            <p className="text-[#A3A3A3]">
              <span className="font-semibold text-white">{heroData.userCount.toLocaleString()}+</span> developers using our marketplace
            </p>
          </div>
        </div>

        {/* 3D Element/Graphics */}
        <div className={`hidden md:block relative w-96 h-96 transition-all duration-1000 delay-300 ease-out transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {/* Floating elements */}
          <div className="absolute top-20 left-10 w-24 h-24 rounded-xl bg-gradient-to-br from-[#7F56D9] to-[#F670C7] opacity-80 transform rotate-12 animate-float shadow-lg shadow-[#7F56D9]/20"></div>
          <div className="absolute top-40 right-10 w-16 h-16 rounded-xl bg-gradient-to-br from-[#F670C7] to-[#7F56D9] opacity-70 transform -rotate-12 animate-float-delay shadow-lg shadow-[#F670C7]/20"></div>
          <div className="absolute bottom-20 left-20 w-20 h-20 rounded-full bg-gradient-to-br from-[#7F56D9] to-[#EA4CFF] opacity-80 animate-float-delay shadow-lg shadow-[#EA4CFF]/20"></div>

          {/* Central "product" element */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-2xl glass shadow-2xl overflow-hidden">
            <div className="h-full w-full bg-gradient-to-br from-[#2A2633]/80 to-[#342D3F]/80 p-4 backdrop-blur-md">
              <div className="w-full h-3/5 rounded-lg bg-gradient-to-br from-[#7F56D9] to-[#F670C7] mb-4 overflow-hidden relative">
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
                <div className="absolute bottom-2 right-2 flex space-x-1">
                  <div className="w-2 h-2 rounded-full bg-white opacity-70"></div>
                  <div className="w-2 h-2 rounded-full bg-white opacity-40"></div>
                  <div className="w-2 h-2 rounded-full bg-white opacity-20"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-3/4 rounded-full bg-[#A3A3A3]/30"></div>
                <div className="h-4 w-1/2 rounded-full bg-[#A3A3A3]/20"></div>
                <div className="flex justify-between items-center mt-4">
                  <div className="h-6 w-6 rounded-full bg-[#F670C7]"></div>
                  <div className="h-8 w-16 rounded-lg bg-[#7F56D9]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-24 overflow-hidden">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="absolute bottom-0 w-full h-auto">
          <path fill="#1F1A27" fillOpacity="1" d="M0,160L40,149.3C80,139,160,117,240,117.3C320,117,400,139,480,149.3C560,160,640,160,720,149.3C800,139,880,117,960,112C1040,107,1120,117,1200,133.3C1280,149,1360,171,1400,181.3L1440,192L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
